import { useState, useRef, useEffect } from 'react';
import { computeOptionsMenuPosition } from '../lib/computeOptionsMenuPosition';

/**
 * Options-menu (gear / mobile-options popover) state + listeners for the
 * calendar module (#3277).
 *
 * Owns `showOptionsMenu`, `optionsMenuPosition`, `optionsMenuRef` plus two effects:
 *   1. `xavior:open-module-options` window listener — positions (via the pure
 *      `computeOptionsMenuPosition`) and opens the popover, but only for THIS
 *      module id. Reads `moduleRootRef.current` lazily for fallback placement.
 *      Dep array is `[moduleId]`.
 *   2. document `mousedown` click-outside listener — closes the menu unless the
 *      click is inside the menu or on an item delete button. Dep `[showOptionsMenu]`.
 *
 * Must be called BEFORE `useCalendarSync` (which receives `showOptionsMenu`) to
 * keep hook order/count stable. `optionsMenuRef` is returned by identity so the
 * JSX container and the click-outside guard read the same node.
 */
export interface UseEventOptionsMenuOptions {
  moduleId: string;
  moduleRootRef: React.RefObject<HTMLDivElement | null>;
}

export function useEventOptionsMenu(opts: UseEventOptionsMenuOptions) {
  const { moduleId, moduleRootRef } = opts;

  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const [optionsMenuPosition, setOptionsMenuPosition] =
    useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const handleOpenOptions = (event: CustomEvent) => {
      const {
        moduleId: eventModuleId,
        anchorRect,
      } = (event.detail || {}) as {
        moduleId?: string;
        anchorRect?: { top: number; right: number; bottom: number; left: number };
      };
      if (eventModuleId !== moduleId) return;

      const moduleRect = !anchorRect && moduleRootRef.current
        ? moduleRootRef.current.getBoundingClientRect()
        : null;

      const { top, left } = computeOptionsMenuPosition(
        window.innerWidth,
        window.innerHeight,
        anchorRect,
        moduleRect,
      );

      setOptionsMenuPosition({ top, left });
      setShowOptionsMenu(true);
    };
    window.addEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
    return () => window.removeEventListener('xavior:open-module-options', handleOpenOptions as EventListener);
  }, [moduleId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showOptionsMenu) return;

      const target = event.target as HTMLElement;

      // Don't close if clicking the menu dropdown or an item delete button.
      if (optionsMenuRef.current?.contains(target)) return;
      const isDeleteButton = target.closest('button[title="Delete"]');
      if (isDeleteButton) return;

      setShowOptionsMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptionsMenu]);

  return {
    showOptionsMenu,
    setShowOptionsMenu,
    optionsMenuPosition,
    optionsMenuRef,
  };
}
